import api from './api';
import { User, ExternalUser } from './auth';
import { ChatSession, ChatMessage } from './chat';

export interface AdminStats {
  users: {
    total: number;
    syncedUsers: number;
  };
  chat: {
    totalSessions: number;
    activeSessions: number;
    totalMessages: number;
  };
  overview: {
    totalUsers: number;
    syncedUsers: number;
    totalChatSessions: number;
    activeChatSessions: number;
    totalMessages: number;
  };
}

export interface UpdateUserRequest {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  company?: string;
  linkedin?: string;
  primaryRole?: string;
  hasPassword?: boolean;
  role?: 'user' | 'admin' | 'super-admin';
  status?: 'pending' | 'approved' | 'rejected';
}

export interface UpdateLocalUserRequest {
  firstName?: string;
  lastName?: string;
  email?: string;
  role?: 'user' | 'admin' | 'super-admin';
}

export interface CreateLocalUserRequest {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  role: 'user' | 'admin' | 'super-admin';
}

export const adminService = {
  async getStats(): Promise<AdminStats> {
    const response = await api.get('/admin/dashboard');
    return response.data;
  },

  async getAllUsers(): Promise<ExternalUser[]> {
    const response = await api.get('/users');
    return response.data;
  },

  async getDashboardUsers(params?: {
    sortBy?: string;
    sortOrder?: string;
    status?: string[];
    search?: string;
  }): Promise<ExternalUser[]> {
    const response = await api.get('/users/dashboard', { params });
    return response.data;
  },

  async getLocalUsers(): Promise<User[]> {
    const response = await api.get('/admin/users');
    return response.data;
  },

  async createLocalUser(data: CreateLocalUserRequest): Promise<User> {
    const response = await api.post('/admin/users', data);
    return response.data;
  },

  async updateLocalUser(
    id: string,
    data: UpdateLocalUserRequest
  ): Promise<User> {
    const response = await api.put(`/admin/users/${id}`, data);
    return response.data;
  },

  async updateUser(id: string, data: UpdateUserRequest): Promise<ExternalUser> {
    const response = await api.put(`/users/${id}`, data);
    return response.data;
  },

  async deleteUser(id: string): Promise<void> {
    await api.delete(`/users/${id}`);
  },

  async getAllChatSessions(): Promise<ChatSession[]> {
    const response = await api.get('/admin/chat/sessions');
    return response.data;
  },

  async getChatSessionsByUserId(userId: string): Promise<ChatSession[]> {
    const response = await api.get(`/admin/chat/sessions/user/${userId}`);
    return response.data;
  },

  async getChatSessionById(id: string): Promise<ChatSession> {
    const response = await api.get(`/admin/chat/sessions/${id}`);
    return response.data;
  },

  async deleteChatSession(id: string): Promise<void> {
    await api.delete(`/admin/chat/sessions/${id}`);
  },

  async getChatSessionMessages(sessionId: string): Promise<ChatMessage[]> {
    const response = await api.get(
      `/admin/chat/sessions/${sessionId}/messages`
    );
    return response.data;
  },

  async forceSyncUsers(): Promise<{
    success: boolean;
    message: string;
    data?: {
      totalExternalUsers: number;
      newUsers: number;
      updatedUsers: number;
      errors: string[];
    };
  }> {
    const response = await api.post('/admin/user-sync/force');
    return response.data;
  },
};

// Re-export types for convenience
export type { ChatSession, ChatMessage };
